import * as Sentry from "@sentry/nextjs";
import { canvasElementPrefix, canvasMetadataPrefix } from "shared/util/utils";
import { prepareCopyForeignImages } from "./file-utils";
import { copyFiles } from "frontend/api";
import BoundingBox from "frontend/geometry/bounding-box";
import { map } from "rambda";
import { isOfTypes } from "frontend/canvas-designer-new/elements/canvas-elements-utils";
import consts from "shared/consts";
import { formatBigNumber } from "./math-utils";
import { calculateOrgChartDimensions } from "frontend/canvas-designer-new/elements/org-chart/orgchart-utils";

export function separateRawDataToElementsAndMetadata(rawData: any) {
  const elements: Record<string, any> = {};
  const metadata: Record<string, any> = {};
  for (const [id, value] of Object.entries(rawData)) {
    if (id.startsWith(canvasElementPrefix)) {
      elements[id.substring(canvasElementPrefix.length)] = value;
    } else if (id.startsWith(canvasMetadataPrefix)) {
      metadata[id.substring(canvasMetadataPrefix.length)] = value;
    }
  }
  return { elements, metadata };
}

export function splitDataByMetadataPrefix(rawData: any) {
  const elements: Record<string, any> = {};
  const metadata: Record<string, any> = {};
  for (const [id, value] of Object.entries(rawData)) {
    if (id.startsWith(canvasMetadataPrefix)) {
      metadata[id] = value;
    } else {
      elements[id] = value;
    }
  }
  return { elements, metadata };
}

export async function createNewFileMetadata(
  fileMetadata: Record<string, any>,
  newDocumentId: string,
  accountId: string,
  fileKeyPrefix: string
) {
  const fileMetadataArray = Object.entries(fileMetadata) as [string, any][];
  if (fileMetadataArray.length > 0) {
    const copyReqDescriptor = prepareCopyForeignImages({
      metadata: fileMetadataArray,
      documentId: newDocumentId,
      fileKeyPrefix,
      accountId: accountId,
    });
    if (Object.keys(copyReqDescriptor).length > 0) {
      try {
        const copyResults = await copyFiles(copyReqDescriptor);
        for (let i = 0; i < fileMetadataArray.length; i++) {
          if (
            "url" in fileMetadataArray[i][1] &&
            fileMetadataArray[i][1].url in copyReqDescriptor &&
            copyResults[fileMetadataArray[i][1].url] == true
          ) {
            fileMetadataArray[i][1].url = copyReqDescriptor[fileMetadataArray[i][1].url];
          }
        }
      } catch (error) {
        Sentry.captureException(error, { tags: { type: "copy-images" } });
      }
    }
  }
  return Object.fromEntries(fileMetadataArray);
}

export async function prepareTemplateDataWithFiles({
  templateData,
  newDocumentId,
  accountId,
}: {
  templateData: any;
  newDocumentId: string;
  accountId: string;
}) {
  let { elements, metadata } = splitDataByMetadataPrefix(templateData);
  metadata = await createNewFileMetadata(metadata, newDocumentId, accountId, "cMetadata-File");
  return { ...elements, ...metadata };
}

function hasValidPosition(element: any) {
  return Number.isFinite(element.x) && Number.isFinite(element.y);
}

const shouldNotHavePosition = isOfTypes(consts.CANVAS_ELEMENTS.TABLE_CELL);

/**
 * Change the (x,y) of elements so the entire group is centered around the origin (0,0).
 * What that means is that if you'll take the bounding-box of all elements, the center of the
 * box will be in 0,0.
 * Warning: mutates elements in place
 * @param elements
 * @returns elements after adjustment (mutated)
 */
export function centerElementsAroundOrigin(elements: Record<string, any>): Record<string, any> {
  const boundingBox = new BoundingBox();
  for (const key in elements) {
    const element = elements[key];
    // FIX: Some table cells managed to get x and y when copy pasted.
    // We NEED to remove this or the templates will be misplaced.
    if (shouldNotHavePosition(key)) {
      delete element.x;
      delete element.y;
    }

    // Check that element has x,y. Some elements (table-cell, mindmap nodes, etc) don't have position,
    // but are placed by their container.
    if (hasValidPosition(element)) {
      boundingBox.expandPoint(element.x, element.y);
    }
  }

  // create a function to offset an element by the center of the bounding box
  const center = boundingBox.center;
  const offsetElement = (element: any): any => {
    return hasValidPosition(element) ? { ...element, x: element.x - center.x, y: element.y - center.y } : element;
  };

  return map(offsetElement, elements);
}

export default function getTemplateDownloadsCount(downloadsCount: number | undefined) {
  if (!downloadsCount || downloadsCount < consts.MIN_DOWNLOADS_COUNT_TO_SHOW_DOWNLOADS_COUNT) {
    return null;
  }

  return formatBigNumber(downloadsCount);
}

export function measureTemplate(
  templateData: Record<string, unknown>
): { width: number; height: number; boundingBox: BoundingBox } | null {
  try {
    const { elements } = separateRawDataToElementsAndMetadata(templateData);
    const centeredData = centerElementsAroundOrigin(elements);
    const elementValues = Object.values(centeredData);

    if (elementValues.length === 0) {
      return null;
    }

    // Calculate bounding box from element properties
    const elementsBoundingBox = BoundingBox.expandOnAll(
      elementValues.map((element: unknown) => {
        const elementData = element as {
          type?: string;
          x?: number;
          y?: number;
          width?: number;
          height?: number;
          scaleX?: number;
          scaleY?: number;
          layout?: Record<string, any>;
          data?: Record<string, any>;
        };

        let elementWidth = elementData.width || 0;
        let elementHeight = elementData.height || 0;

        if (elementData.type === consts.CANVAS_ELEMENTS.ORG_CHART && (elementWidth === 0 || elementHeight === 0)) {
          const layout = elementData.layout || {};
          const data = elementData.data || {};
          const dimensions = calculateOrgChartDimensions(layout, data);
          elementWidth = dimensions.width;
          elementHeight = dimensions.height;
        } else {
          elementWidth = elementWidth || 100;
          elementHeight = elementHeight || 100;
        }

        return {
          x: elementData.x || 0,
          y: elementData.y || 0,
          width: elementWidth * (elementData.scaleX || 1),
          height: elementHeight * (elementData.scaleY || 1),
        };
      })
    );

    if (!BoundingBox.isValid(elementsBoundingBox)) {
      return null;
    }

    return {
      width: elementsBoundingBox.width,
      height: elementsBoundingBox.height,
      boundingBox: elementsBoundingBox,
    };
  } catch (error) {
    console.error("Error in template measurement:", error);
    return null;
  }
}
